import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    ChangePasswordRequestApiObject,
    ForgotPasswordRequestApiObject,
    ResetPasswordRequestApiObject,
    SignInCodeRequestApiObject,
    SignInCodeResponseApiObject,
    SignInRequestApiObject,
    SignInResponseApiObject,
    SignUpRequestApiObject,
    SignUpResponseApiObject,
    VerifyMfaRequestApiObject,
} from 'app/core/api/auth';
import { EmptyApiObject } from 'app/core/api/common';
import { environment } from 'env/environment';
import { Observable } from 'rxjs';

/**
 * Transport for /api/auth — one method per route, and nothing else.
 *
 * No session state, no token storage, no navigation: AuthService owns all of that and calls
 * through here. Bodies are typed as the request ApiObject, which excess-property-checks an object
 * literal at the call site the same way `satisfies` does at a bare http.post.
 *
 * Every one of these routes can also reply `AuthMessageApiObject`, always with a 4xx, which
 * HttpClient delivers on the error channel rather than to the subscriber. The return types below
 * are therefore the success shape only — read `error.error.message` for the other arm.
 */
@Injectable({providedIn: 'root'})
export class AuthApi {

    private readonly http = inject(HttpClient);
    private readonly base = `${environment.apiUrl}/auth`;

    signUp(body: SignUpRequestApiObject): Observable<SignUpResponseApiObject> {
        return this.http.post<SignUpResponseApiObject>(`${this.base}/sign-up`, body);
    }

    /** First leg: checks the password and reports which factor to satisfy next. Mints no session. */
    signIn(body: SignInRequestApiObject): Observable<SignInResponseApiObject> {
        return this.http.post<SignInResponseApiObject>(`${this.base}/sign-in`, body);
    }

    /** Second leg: answers the factor and mints the session token. */
    signInCode(body: SignInCodeRequestApiObject): Observable<SignInCodeResponseApiObject> {
        return this.http.post<SignInCodeResponseApiObject>(`${this.base}/sign-in-code`, body);
    }

    /** Completes a first-time enrolment, which also mints the session. */
    verifyMfa(body: VerifyMfaRequestApiObject): Observable<SignInCodeResponseApiObject> {
        return this.http.post<SignInCodeResponseApiObject>(`${this.base}/verify-mfa`, body);
    }

    /** Always 200, whether or not the address has an account. */
    forgotPassword(body: ForgotPasswordRequestApiObject): Observable<EmptyApiObject> {
        return this.http.post<EmptyApiObject>(`${this.base}/forgot-password`, body);
    }

    resetPassword(body: ResetPasswordRequestApiObject): Observable<EmptyApiObject> {
        return this.http.post<EmptyApiObject>(`${this.base}/reset-password`, body);
    }

    changePassword(body: ChangePasswordRequestApiObject): Observable<EmptyApiObject> {
        return this.http.post<EmptyApiObject>(`${this.base}/change-password`, body);
    }
}
